import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  createDashboardUser,
  fetchDashboardUsers,
  getApiErrorMessage,
  resetDashboardUserPassword,
  updateDashboardUser,
} from "../api";
import ErrorRetry from "../components/ErrorRetry";
import {
  PERMISSION_CATALOG,
  ROLE_PRESETS,
} from "../permissions";

const EMPTY_FORM = {
  username: "",
  password: "",
  role: "admin",
  display_name: "",
  permissions: [...ROLE_PRESETS.admin],
};

const ROLE_FILTERS = [
  { value: "all", label: "Все роли" },
  { value: "admin", label: "Админ" },
  { value: "manager", label: "Руководитель" },
];

const STATUS_FILTERS = [
  { value: "all", label: "Все" },
  { value: "active", label: "Активны" },
  { value: "inactive", label: "Отключены" },
];

function initials(displayName, username) {
  const source = String(displayName || username || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (source.length === 0) return "?";
  if (source.length === 1) return source[0].slice(0, 2).toUpperCase();
  return source
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join("");
}

function roleLabel(role) {
  return role === "manager" ? "Руководитель" : "Админ";
}

function permissionSummary(permissions) {
  if (!permissions?.length) return "нет прав";
  const labels = PERMISSION_CATALOG.filter((p) => permissions.includes(p.code)).map(
    (p) => p.label
  );
  if (labels.length <= 3) return labels.join(", ");
  return `${labels.slice(0, 3).join(", ")} +${labels.length - 3}`;
}

function PermissionsEditor({ value, onChange, disabled }) {
  const toggle = (code) => {
    if (disabled) return;
    if (value.includes(code)) {
      onChange(value.filter((c) => c !== code));
    } else {
      onChange([...value, code]);
    }
  };

  return (
    <div className="users-permissions-grid">
      {PERMISSION_CATALOG.map(({ code, label }) => (
        <label key={code} className="users-permission-item">
          <input
            type="checkbox"
            checked={value.includes(code)}
            onChange={() => toggle(code)}
            disabled={disabled}
          />
          <span>{label}</span>
          <code className="users-permission-code">{code}</code>
        </label>
      ))}
    </div>
  );
}

function useModalFocus(active, containerRef) {
  useEffect(() => {
    if (!active || !containerRef.current) return undefined;
    const root = containerRef.current;
    const previouslyFocused = document.activeElement;
    const selector =
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const focusables = () => Array.from(root.querySelectorAll(selector));
    const first = focusables()[0];
    if (first) first.focus();

    const onKeyDown = (event) => {
      if (event.key !== "Tab") return;
      const list = focusables();
      if (list.length === 0) return;
      const idx = list.indexOf(document.activeElement);
      if (event.shiftKey) {
        if (idx <= 0) {
          event.preventDefault();
          list[list.length - 1].focus();
        }
      } else if (idx === list.length - 1) {
        event.preventDefault();
        list[0].focus();
      }
    };

    root.addEventListener("keydown", onKeyDown);
    return () => {
      root.removeEventListener("keydown", onKeyDown);
      if (previouslyFocused instanceof HTMLElement) previouslyFocused.focus();
    };
  }, [active, containerRef]);
}

export default function Users() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [form, setForm] = useState(EMPTY_FORM);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [resetPassword, setResetPassword] = useState("");
  const [resetting, setResetting] = useState(false);
  const [actionError, setActionError] = useState("");
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [query, setQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [deactivateTarget, setDeactivateTarget] = useState(null);
  const [togglingId, setTogglingId] = useState(null);

  const createModalRef = useRef(null);
  const editModalRef = useRef(null);
  const resetModalRef = useRef(null);
  const deactivateModalRef = useRef(null);

  useModalFocus(createOpen, createModalRef);
  useModalFocus(Boolean(editTarget), editModalRef);
  useModalFocus(Boolean(resetTarget), resetModalRef);
  useModalFocus(Boolean(deactivateTarget), deactivateModalRef);

  const loadUsers = useCallback(async ({ soft = false } = {}) => {
    if (!soft) setLoading(true);
    setError("");
    try {
      const data = await fetchDashboardUsers();
      setUsers(data);
    } catch (err) {
      setError(getApiErrorMessage(err, "Не удалось загрузить пользователей"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const summary = useMemo(() => {
    const active = users.filter((u) => u.is_active).length;
    const inactive = users.length - active;
    const admins = users.filter((u) => u.role === "admin").length;
    const managers = users.filter((u) => u.role === "manager").length;
    return { total: users.length, active, inactive, admins, managers };
  }, [users]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return users.filter((user) => {
      if (roleFilter !== "all" && user.role !== roleFilter) return false;
      if (statusFilter === "active" && !user.is_active) return false;
      if (statusFilter === "inactive" && user.is_active) return false;
      if (!q) return true;
      return (
        user.username.toLowerCase().includes(q) ||
        (user.display_name || "").toLowerCase().includes(q)
      );
    });
  }, [users, query, roleFilter, statusFilter]);

  const closeCreate = () => {
    if (creating) return;
    setCreateOpen(false);
    setForm({ ...EMPTY_FORM, permissions: [...ROLE_PRESETS.admin] });
    setActionError("");
  };

  const handleCreate = async (event) => {
    event.preventDefault();
    setCreating(true);
    setActionError("");
    try {
      await createDashboardUser({
        username: form.username.trim(),
        password: form.password,
        role: form.role,
        display_name: form.display_name.trim() || null,
        permissions: form.permissions,
      });
      setForm({ ...EMPTY_FORM, permissions: [...ROLE_PRESETS.admin] });
      setCreateOpen(false);
      await loadUsers({ soft: true });
    } catch (err) {
      setActionError(getApiErrorMessage(err, "Не удалось создать пользователя"));
    } finally {
      setCreating(false);
    }
  };

  const requestToggleActive = (user) => {
    setActionError("");
    if (user.is_active) {
      setDeactivateTarget(user);
      return;
    }
    void applyToggleActive(user);
  };

  const applyToggleActive = async (user) => {
    setTogglingId(user.id);
    setActionError("");
    try {
      await updateDashboardUser(user.id, { is_active: !user.is_active });
      setDeactivateTarget(null);
      await loadUsers({ soft: true });
    } catch (err) {
      setActionError(getApiErrorMessage(err, "Не удалось обновить пользователя"));
    } finally {
      setTogglingId(null);
    }
  };

  const openEdit = (user) => {
    setEditTarget(user);
    setEditForm({
      role: user.role,
      display_name: user.display_name || "",
      permissions: [...(user.permissions || [])],
    });
    setActionError("");
  };

  const closeEdit = () => {
    if (savingEdit) return;
    setEditTarget(null);
    setEditForm(null);
  };

  const handleSaveEdit = async (event) => {
    event.preventDefault();
    if (!editTarget || !editForm) return;
    setSavingEdit(true);
    setActionError("");
    try {
      await updateDashboardUser(editTarget.id, {
        role: editForm.role,
        display_name: editForm.display_name.trim() || null,
        permissions: editForm.permissions,
      });
      setEditTarget(null);
      setEditForm(null);
      await loadUsers({ soft: true });
    } catch (err) {
      setActionError(getApiErrorMessage(err, "Не удалось сохранить права"));
    } finally {
      setSavingEdit(false);
    }
  };

  const closeReset = () => {
    if (resetting) return;
    setResetTarget(null);
    setResetPassword("");
    setActionError("");
  };

  const handleResetPassword = async (event) => {
    event.preventDefault();
    if (!resetTarget) return;
    setResetting(true);
    setActionError("");
    try {
      await resetDashboardUserPassword(resetTarget.id, resetPassword);
      setResetTarget(null);
      setResetPassword("");
    } catch (err) {
      setActionError(getApiErrorMessage(err, "Не удалось сбросить пароль"));
    } finally {
      setResetting(false);
    }
  };

  useEffect(() => {
    if (!createOpen && !editTarget && !resetTarget && !deactivateTarget) return undefined;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (creating || savingEdit || resetting || togglingId) return;
      setCreateOpen(false);
      setForm({ ...EMPTY_FORM, permissions: [...ROLE_PRESETS.admin] });
      setEditTarget(null);
      setEditForm(null);
      setResetTarget(null);
      setResetPassword("");
      setDeactivateTarget(null);
      setActionError("");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [
    createOpen,
    editTarget,
    resetTarget,
    deactivateTarget,
    creating,
    savingEdit,
    resetting,
    togglingId,
  ]);

  const anyModalOpen = createOpen || editTarget || resetTarget || deactivateTarget;

  return (
    <div className="page-container dash-users-page">
      <div className="page-header dash-users-header">
        <div>
          <h2>Пользователи</h2>
          <p className="page-subtitle dash-users-subtitle">
            Управление доступом и правами дашборда
          </p>
        </div>
        <div className="dash-users-header-actions">
          <button
            type="button"
            className="dash-users-add-btn"
            onClick={() => {
              setActionError("");
              setForm({ ...EMPTY_FORM, permissions: [...ROLE_PRESETS.admin] });
              setCreateOpen(true);
            }}
          >
            Добавить
          </button>
        </div>
      </div>

      {error && <ErrorRetry message={error} onRetry={loadUsers} />}
      {actionError && !anyModalOpen && (
        <div className="login-error" role="alert">
          {actionError}
        </div>
      )}

      {!error && (
        <section className="dash-users-metrics" aria-label="Сводка по пользователям">
          <div className="dash-users-metric">
            <span>Всего</span>
            <strong>{summary.total}</strong>
          </div>
          <div className="dash-users-metric">
            <span>Активны</span>
            <strong>{summary.active}</strong>
          </div>
          <div className="dash-users-metric">
            <span>Отключены</span>
            <strong>{summary.inactive}</strong>
          </div>
          <div className="dash-users-metric">
            <span>Админы</span>
            <strong>{summary.admins}</strong>
          </div>
          <div className="dash-users-metric">
            <span>Руководители</span>
            <strong>{summary.managers}</strong>
          </div>
        </section>
      )}

      <section className="dash-users-table-section">
        <div className="dash-users-toolbar">
          <label className="dash-users-search">
            <span className="dash-users-visually-hidden">Поиск пользователей</span>
            <input
              type="search"
              name="users-search"
              autoComplete="off"
              placeholder="Логин или имя…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              aria-label="Поиск по логину или имени"
            />
          </label>
          <label className="dash-users-filter">
            <span>Роль</span>
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              aria-label="Фильтр роли"
            >
              {ROLE_FILTERS.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
          </label>
          <label className="dash-users-filter">
            <span>Статус</span>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              aria-label="Фильтр статуса"
            >
              {STATUS_FILTERS.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
          </label>
          <span className="dash-users-count" aria-live="polite">
            Показано {filtered.length} из {users.length}
          </span>
        </div>

        {loading ? (
          <p className="settings-hint">Загрузка…</p>
        ) : filtered.length === 0 ? (
          <div className="dash-users-empty">
            <p className="dash-users-empty-title">
              {users.length === 0 ? "Список пользователей пуст" : "Ничего не найдено"}
            </p>
            <p className="dash-users-empty-text">
              {users.length === 0
                ? "Добавьте первого пользователя дашборда."
                : "Измените поиск или фильтры."}
            </p>
            {users.length === 0 && (
              <button
                type="button"
                className="dash-users-add-btn"
                onClick={() => {
                  setActionError("");
                  setCreateOpen(true);
                }}
              >
                Добавить
              </button>
            )}
          </div>
        ) : (
          <div className="dash-users-table-wrap">
            <table className="dash-users-table">
              <thead>
                <tr>
                  <th>Пользователь</th>
                  <th>Роль</th>
                  <th>Права</th>
                  <th>Активен</th>
                  <th>Действия</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((user) => {
                  const busy = togglingId === user.id;
                  return (
                    <tr key={user.id}>
                      <td>
                        <div className="employee-cell">
                          <div className="employee-avatar" aria-hidden="true">
                            {initials(user.display_name, user.username)}
                          </div>
                          <div className="employee-info">
                            <div className="employee-name">{user.username}</div>
                            <div className="employee-role">
                              {user.display_name || "без отображаемого имени"}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className="cd-badge cd-badge--accent">{roleLabel(user.role)}</span>
                      </td>
                      <td className="dash-users-permissions-summary">
                        {permissionSummary(user.permissions)}
                      </td>
                      <td>
                        <button
                          type="button"
                          className={`staff-switch ${user.is_active ? "staff-switch--on" : ""}`}
                          aria-pressed={user.is_active}
                          aria-label={
                            user.is_active
                              ? `${user.username}: активен`
                              : `${user.username}: отключён`
                          }
                          disabled={busy}
                          onClick={() => requestToggleActive(user)}
                        >
                          <span className="staff-switch-track" aria-hidden="true" />
                          <span className="staff-switch-label">
                            {user.is_active ? "Да" : "Нет"}
                          </span>
                        </button>
                      </td>
                      <td className="dash-users-actions">
                        <button
                          type="button"
                          className="users-action-btn"
                          onClick={() => openEdit(user)}
                        >
                          Права
                        </button>
                        <button
                          type="button"
                          className="users-action-btn"
                          onClick={() => {
                            setResetTarget(user);
                            setResetPassword("");
                            setActionError("");
                          }}
                        >
                          Пароль
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {createOpen && (
        <div className="users-modal-backdrop staff-modal-backdrop" onClick={closeCreate}>
          <div
            ref={createModalRef}
            className="users-modal users-modal-wide staff-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="dash-users-create-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="dash-users-create-title">Новый пользователь</h3>
            {actionError && (
              <div className="login-error" role="alert">
                {actionError}
              </div>
            )}
            <form className="users-form-grid" onSubmit={handleCreate}>
              <label className="login-label">
                Логин
                <input
                  className="login-input"
                  name="username"
                  autoComplete="off"
                  spellCheck={false}
                  value={form.username}
                  onChange={(e) => setForm({ ...form, username: e.target.value })}
                  required
                  disabled={creating}
                />
              </label>
              <label className="login-label">
                Пароль
                <input
                  type="password"
                  className="login-input"
                  name="password"
                  autoComplete="new-password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  minLength={8}
                  required
                  disabled={creating}
                />
              </label>
              <label className="login-label">
                Пресет роли
                <select
                  className="login-input"
                  name="role"
                  value={form.role}
                  onChange={(e) => {
                    const role = e.target.value;
                    setForm({
                      ...form,
                      role,
                      permissions: [...(ROLE_PRESETS[role] || [])],
                    });
                  }}
                  disabled={creating}
                >
                  <option value="manager">Руководитель</option>
                  <option value="admin">Админ</option>
                </select>
              </label>
              <label className="login-label">
                Отображаемое имя
                <input
                  className="login-input"
                  name="display_name"
                  autoComplete="name"
                  value={form.display_name}
                  onChange={(e) => setForm({ ...form, display_name: e.target.value })}
                  disabled={creating}
                />
              </label>
              <div className="users-permissions-block">
                <div className="users-permissions-title">Права доступа</div>
                <PermissionsEditor
                  value={form.permissions}
                  onChange={(permissions) => setForm({ ...form, permissions })}
                  disabled={creating}
                />
              </div>
              <div className="users-form-actions">
                <button
                  type="button"
                  className="users-action-btn"
                  onClick={closeCreate}
                  disabled={creating}
                >
                  Отмена
                </button>
                <button type="submit" className="login-button" disabled={creating}>
                  {creating ? "Создание…" : "Создать"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {editTarget && editForm && (
        <div className="users-modal-backdrop staff-modal-backdrop" onClick={closeEdit}>
          <div
            ref={editModalRef}
            className="users-modal users-modal-wide staff-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="dash-users-edit-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="dash-users-edit-title">Права: {editTarget.username}</h3>
            {actionError && (
              <div className="login-error" role="alert">
                {actionError}
              </div>
            )}
            <form onSubmit={handleSaveEdit}>
              <label className="login-label">
                Пресет роли
                <select
                  className="login-input"
                  name="role"
                  value={editForm.role}
                  onChange={(e) => {
                    const role = e.target.value;
                    setEditForm({
                      ...editForm,
                      role,
                      permissions: [...(ROLE_PRESETS[role] || [])],
                    });
                  }}
                  disabled={savingEdit}
                >
                  <option value="manager">Руководитель</option>
                  <option value="admin">Админ</option>
                </select>
              </label>
              <label className="login-label">
                Отображаемое имя
                <input
                  className="login-input"
                  name="display_name"
                  autoComplete="name"
                  value={editForm.display_name}
                  onChange={(e) =>
                    setEditForm({ ...editForm, display_name: e.target.value })
                  }
                  disabled={savingEdit}
                />
              </label>
              <div className="users-permissions-block">
                <div className="users-permissions-title">Права доступа</div>
                <PermissionsEditor
                  value={editForm.permissions}
                  onChange={(permissions) => setEditForm({ ...editForm, permissions })}
                  disabled={savingEdit}
                />
              </div>
              <div className="users-modal-actions">
                <button
                  type="button"
                  className="users-action-btn"
                  onClick={closeEdit}
                  disabled={savingEdit}
                >
                  Отмена
                </button>
                <button type="submit" className="login-button" disabled={savingEdit}>
                  {savingEdit ? "Сохранение…" : "Сохранить"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {resetTarget && (
        <div className="users-modal-backdrop staff-modal-backdrop" onClick={closeReset}>
          <div
            ref={resetModalRef}
            className="users-modal staff-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="dash-users-reset-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="dash-users-reset-title">Сброс пароля: {resetTarget.username}</h3>
            {actionError && (
              <div className="login-error" role="alert">
                {actionError}
              </div>
            )}
            <form onSubmit={handleResetPassword}>
              <label className="login-label">
                Новый пароль
                <input
                  type="password"
                  className="login-input"
                  name="new_password"
                  autoComplete="new-password"
                  value={resetPassword}
                  onChange={(e) => setResetPassword(e.target.value)}
                  minLength={8}
                  required
                  disabled={resetting}
                />
              </label>
              <div className="users-modal-actions">
                <button
                  type="button"
                  className="users-action-btn"
                  onClick={closeReset}
                  disabled={resetting}
                >
                  Отмена
                </button>
                <button type="submit" className="login-button" disabled={resetting}>
                  {resetting ? "Сохранение…" : "Сохранить"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deactivateTarget && (
        <div
          className="users-modal-backdrop staff-modal-backdrop"
          onClick={() => !togglingId && setDeactivateTarget(null)}
        >
          <div
            ref={deactivateModalRef}
            className="users-modal staff-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="dash-users-deactivate-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="dash-users-deactivate-title">Отключить пользователя?</h3>
            <p className="settings-hint">
              «{deactivateTarget.username}» потеряет доступ к дашборду, пока аккаунт не
              включат снова.
            </p>
            {actionError && (
              <div className="login-error" style={{ marginTop: 12 }} role="alert">
                {actionError}
              </div>
            )}
            <div className="users-modal-actions">
              <button
                type="button"
                className="users-action-btn"
                onClick={() => setDeactivateTarget(null)}
                disabled={Boolean(togglingId)}
              >
                Отмена
              </button>
              <button
                type="button"
                className="cd-danger-btn"
                onClick={() => applyToggleActive(deactivateTarget)}
                disabled={Boolean(togglingId)}
              >
                {togglingId ? "Отключение…" : "Отключить"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
